import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";
import { LlamaWasmWorker, type MountedFile } from "@zhoukezi/llama-wasm";
import StartOverlay from "./components/StartOverlay.tsx";
import HUD from "./components/HUD.tsx";
import Caption from "./components/Caption.tsx";
import History from "./components/History.tsx";
import Visualizer from "./components/Visualizer.tsx";
import { AudioPipeline } from "./lib/audio-pipeline.ts";
import { Scheduler } from "./lib/scheduler.ts";
import {
  pickLocalFile,
  loadCachedModels,
  downloadAndCacheModels,
  MODEL_REGISTRY,
  type DownloadProgress,
} from "./lib/model-loader.ts";
import {
  phase,
  setPhase,
  setErrorMsg,
  setLoadProgress,
  setDeviceLabel,
  setModelSampleRate,
  setDownloadProgress,
  setDownloadLabel,
  setModelsFromCache,
} from "./lib/store.ts";

const App: Component = () => {
  const [modelName, setModelName] = createSignal("");
  const [mmprojName, setMmprojName] = createSignal("");
  const [pipeline, setPipeline] = createSignal<AudioPipeline | null>(null);

  let modelFile: File | null = null;
  let mmprojFile: File | null = null;
  let client: LlamaWasmWorker | null = null;
  let scheduler: Scheduler | null = null;
  let modelMounted: MountedFile | null = null;
  let mmprojMounted: MountedFile | null = null;

  // Check OPFS cache on mount
  onMount(async () => {
    try {
      const cached = await loadCachedModels();
      if (cached) {
        modelFile = cached.backbone;
        mmprojFile = cached.mmproj;
        setModelName(MODEL_REGISTRY.backbone.name);
        setMmprojName(MODEL_REGISTRY.mmproj.name);
        setModelsFromCache(true);
        console.log("[cache] Models loaded from OPFS cache");
      }
    } catch (err) {
      console.warn("[cache] Failed to check OPFS cache:", err);
    }
  });

  onCleanup(() => {
    scheduler?.stop();
    if (client) {
      const c = client;
      void (async () => {
        await c.unload().catch(() => {});
        if (modelMounted) await c.unmountFile(modelMounted).catch(() => {});
        if (mmprojMounted) await c.unmountFile(mmprojMounted).catch(() => {});
        c.terminate();
      })();
    }
  });

  const handleDownload = async () => {
    setErrorMsg("");
    setDownloadProgress(0);

    try {
      const onProgress = (p: DownloadProgress) => {
        const label =
          p.phase === "backbone"
            ? "Downloading backbone..."
            : "Downloading mmproj...";
        setDownloadLabel(label);

        if (p.total > 0) {
          // Weight: backbone = 0..0.7, mmproj = 0.7..1.0
          const phaseRatio = p.loaded / p.total;
          const overall =
            p.phase === "backbone"
              ? phaseRatio * 0.7
              : 0.7 + phaseRatio * 0.3;
          setDownloadProgress(overall);
        }
      };

      const result = await downloadAndCacheModels(onProgress);
      modelFile = result.backbone;
      mmprojFile = result.mmproj;
      setModelName(MODEL_REGISTRY.backbone.name);
      setMmprojName(MODEL_REGISTRY.mmproj.name);
      setModelsFromCache(true);
      console.log("[download] Models downloaded and cached");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
      console.error("[download] Error:", msg, err);
    } finally {
      setDownloadProgress(-1);
      setDownloadLabel("");
    }
  };

  const handleLoadModel = async () => {
    try {
      const { file, name } = await pickLocalFile("Backbone GGUF");
      modelFile = file;
      setModelName(name);
      setModelsFromCache(false);
      setErrorMsg("");
    } catch (err) {
      if (err instanceof Error && err.message.includes("cancel")) return;
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const handleLoadMmproj = async () => {
    try {
      const { file, name } = await pickLocalFile("Mmproj GGUF");
      mmprojFile = file;
      setMmprojName(name);
      setModelsFromCache(false);
      setErrorMsg("");
    } catch (err) {
      if (err instanceof Error && err.message.includes("cancel")) return;
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const handleStart = async () => {
    if (!modelFile || !mmprojFile) return;
    setErrorMsg("");

    try {
      setPhase("loading-model");
      setLoadProgress(-1); // indeterminate

      // 1. Spin up inference worker
      console.log("[start] Creating inference worker...");
      const worker = new Worker(
        new URL("./workers/llama.worker.ts", import.meta.url),
        { type: "module" },
      );
      client = new LlamaWasmWorker(worker);
      console.log("[start] Worker created");

      // 2. Configure runtime
      const PTHREAD_POOL_SIZE = 4;
      await client.setRuntimeConfig({
        nThreads: Math.min(navigator.hardwareConcurrency ?? 4, PTHREAD_POOL_SIZE),
        nCtx: 128,
        temperature: 0,
      });
      console.log("[start] Worker configured");

      // 3. Mount and load backbone model
      setLoadProgress(0.2);
      console.log("[start] Mounting backbone model...");
      modelMounted = await client.mountFile(modelFile);
      console.log("[start] Loading backbone model...");
      await client.loadModel(modelMounted.path);
      modelFile = null; // release reference
      console.log("[start] Backbone loaded");

      // 4. Mount and load mmproj
      setLoadProgress(0.6);
      console.log("[start] Mounting mmproj...");
      mmprojMounted = await client.mountFile(mmprojFile);
      console.log("[start] Loading mmproj...");
      await client.loadMMProj(mmprojMounted.path);
      mmprojFile = null;
      console.log("[start] Mmproj loaded");

      setLoadProgress(0.9);

      // 5. Query model sample rate
      const sr = (await client.getAudioSampleRate()) ?? 16_000;
      console.log("[start] Audio sample rate:", sr);
      setModelSampleRate(sr);

      // 6. Initialise audio pipeline
      console.log("[start] Initialising audio pipeline...");
      const audioPipeline = new AudioPipeline({ targetSampleRate: sr });
      await audioPipeline.init();
      setPipeline(audioPipeline);
      setDeviceLabel(audioPipeline.deviceName);
      console.log("[start] Audio pipeline ready, device:", audioPipeline.deviceName);

      setLoadProgress(1);

      // 7. Start scheduler
      scheduler = new Scheduler(client, audioPipeline);
      scheduler.start();
      console.log("[start] Scheduler started, entering recording phase");

      setPhase("recording");
    } catch (err) {
      setPhase("startup");
      setLoadProgress(-1);
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
      console.error("[start] Error:", msg, err);
    }
  };

  const showUI = () =>
    phase() !== "startup" && phase() !== "loading-model";

  return (
    <>
      <Visualizer pipeline={pipeline()} />

      <StartOverlay
        onDownload={handleDownload}
        onLoadModel={handleLoadModel}
        onLoadMmproj={handleLoadMmproj}
        onStart={handleStart}
        modelName={modelName}
        mmprojName={mmprojName}
      />

      <Show when={showUI()}>
        <HUD />
        <Caption />
        <History />
      </Show>
    </>
  );
};

export default App;
